import { useEffect } from 'react';
import { SessionManager } from '@/lib/sessionManager';
import { useLocation } from 'react-router-dom';

export const useAdvancedTracking = () => {
  const location = useLocation();
  const sessionManager = SessionManager.getInstance();

  useEffect(() => {
    const trackPage = async () => {
      const pathname = location.pathname;
      let pageType = 'other';
      let landingPageId: string | undefined;
      let productId: string | undefined;

      // Determine page type
      if (pathname === '/' || pathname === '/home') {
        pageType = 'home';
      } else if (pathname.startsWith('/landing/')) {
        pageType = 'landing_page';
        landingPageId = pathname.split('/')[2];
      } else if (pathname.startsWith('/product/')) {
        pageType = 'product';
        productId = pathname.split('/')[2];
      } else if (pathname === '/checkout') {
        pageType = 'checkout';
      } else if (pathname === '/thank-you') {
        pageType = 'thank_you';
      } else if (pathname === '/upsell') {
        pageType = 'upsell';
      } else if (pathname === '/downsell') {
        pageType = 'downsell';
      }

      await sessionManager.trackPageView(
        pageType,
        pathname,
        document.title,
        landingPageId,
        productId
      );
    };

    trackPage();
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const elementType = target.tagName.toLowerCase();
      const elementId = target.id || undefined;
      const elementClass = target.className || undefined;
      const elementText = target.textContent?.slice(0, 100) || undefined;

      sessionManager.trackInteraction(
        'click',
        elementType,
        elementId,
        elementClass,
        elementText,
        e.clientX,
        e.clientY
      );

      // Track heatmap for clicks
      sessionManager.trackHeatmap(
        getPageType(),
        location.pathname,
        e.clientX,
        e.clientY,
        'click',
        getElementSelector(target)
      );
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [location.pathname]);

  const getPageType = (): string => {
    const pathname = location.pathname;
    if (pathname === '/' || pathname === '/home') return 'home';
    if (pathname.startsWith('/landing/')) return 'landing_page';
    if (pathname.startsWith('/product/')) return 'product';
    if (pathname === '/checkout') return 'checkout';
    return 'other';
  };

  const getElementSelector = (element: HTMLElement): string => {
    if (element.id) return `#${element.id}`;
    if (element.className) return `.${element.className.split(' ')[0]}`;
    return element.tagName.toLowerCase();
  };

  return {
    trackCustomEvent: (eventName: string, metadata?: Record<string, any>) => {
      sessionManager.trackInteraction(eventName, undefined, undefined, undefined, undefined, undefined, undefined, metadata);
    }
  };
};
